/* Email Templates — management API client.
   Consumed by Settings → Email Templates. The Email Hub Compose
   modal continues to use its existing /email-hub/templates endpoint
   (consumer surface) which reads from the same table. */

import { API_URL } from "../config";
import { trackedFetch } from "../Components/Loader/GlobalLoader";

const headers = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export type EmailTemplateStatusFilter = "all" | "active" | "inactive";

/** Communication template types — drives the picker filter and the
 *  editor's UI shape (subject + Quill for `email`, plain text for the
 *  rest). Mirrors backend EmailHubTemplate::TYPES. */
export type CommunicationTemplateType =
  | "email"
  | "call_log"
  | "internal_note"
  | "meeting_note"
  | "follow_up"
  | "customer_message";

/** Mirrors backend EmailHubTemplate::VISIBILITY. */
export type CommunicationTemplateVisibility =
  | "customer_facing"
  | "internal"
  | "private_staff";

export interface IEmailTemplateUserTag {
  id: number;
  name: string;
  image: string | null;
}

export interface IEmailTemplate {
  id: number;
  name: string;
  slug: string;
  subject: string;
  description: string | null;
  body: string;
  body_text: string | null;
  category: string | null;
  category_label: string | null;
  template_type: CommunicationTemplateType;
  template_type_label: string | null;
  visibility: CommunicationTemplateVisibility;
  visibility_label: string | null;
  sort_order: number;
  is_active: boolean;
  is_default: boolean;
  variables: string[];
  created_by: number | null;
  updated_by: number | null;
  creator: IEmailTemplateUserTag | null;
  updater: IEmailTemplateUserTag | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface IEmailTemplateVariable {
  key: string;
  label: string;
  group: string;
  example: string;
}

export interface IEmailTemplateCategoryOption {
  value: string;
  label: string;
}

export interface IEmailTemplateTypeOption {
  value: CommunicationTemplateType;
  label: string;
}

export interface IEmailTemplateVisibilityOption {
  value: CommunicationTemplateVisibility;
  label: string;
}

export interface IEmailTemplateMeta {
  /** Back-compat — flat list of email categories. New surfaces should
   *  prefer `categories_by_type[type]` instead. */
  categories: IEmailTemplateCategoryOption[];
  variables:  IEmailTemplateVariable[];

  /** Per-type category map — drives the cascading category dropdown
   *  in the editor and the type-aware picker chips elsewhere. */
  categories_by_type: Record<CommunicationTemplateType, IEmailTemplateCategoryOption[]>;
  types: IEmailTemplateTypeOption[];
  visibilities: IEmailTemplateVisibilityOption[];
  /** Recommended default visibility for each type, e.g.
   *  internal_note → private_staff. The editor uses this to seed the
   *  visibility dropdown on type change. */
  default_visibility_by_type: Record<CommunicationTemplateType, CommunicationTemplateVisibility>;
}

export interface IEmailTemplateInput {
  name: string;
  slug?: string;
  subject?: string;
  description?: string;
  body: string;
  body_text?: string;
  category?: string;
  template_type?: CommunicationTemplateType;
  visibility?: CommunicationTemplateVisibility;
  sort_order?: number;
  is_active?: boolean;
  is_default?: boolean;
  variables?: string[];
}

export interface IEmailTemplatePreview {
  subject: string;
  body: string;
  body_text: string;
  context: Record<string, string>;
}

interface ApiEnvelope<T> {
  code: number;
  status: boolean;
  message?: string;
  data: T;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await trackedFetch(`${API_URL}${path}`, { ...init, headers: headers() });
  const json: ApiEnvelope<T> = await res.json();
  if (!json.status) throw new Error(json.message || "Request failed");
  return json.data;
}

/* ── Read ───────────────────────────────────────────────────────── */

export const fetchEmailTemplateMeta = (): Promise<IEmailTemplateMeta> =>
  call<IEmailTemplateMeta>("/email-templates/meta", { method: "GET" });

export const fetchEmailTemplates = (filters: {
  search?: string;
  category?: string;
  status?: EmailTemplateStatusFilter;
  template_type?: CommunicationTemplateType;
  visibility?: CommunicationTemplateVisibility;
} = {}): Promise<IEmailTemplate[]> => {
  const qs = new URLSearchParams();
  if (filters.search)        qs.set("search", filters.search);
  if (filters.category)      qs.set("category", filters.category);
  if (filters.status)        qs.set("status", filters.status);
  if (filters.template_type) qs.set("template_type", filters.template_type);
  if (filters.visibility)    qs.set("visibility", filters.visibility);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return call<IEmailTemplate[]>(`/email-templates${suffix}`, { method: "GET" });
};

export const fetchEmailTemplate = (id: number): Promise<IEmailTemplate> =>
  call<IEmailTemplate>(`/email-templates/${id}`, { method: "GET" });

/* ── Write ──────────────────────────────────────────────────────── */

export const createEmailTemplate = (data: IEmailTemplateInput): Promise<IEmailTemplate> =>
  call<IEmailTemplate>(`/email-templates`, { method: "POST", body: JSON.stringify(data) });

export const updateEmailTemplate = (id: number, data: IEmailTemplateInput): Promise<IEmailTemplate> =>
  call<IEmailTemplate>(`/email-templates/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteEmailTemplate = (id: number): Promise<null> =>
  call<null>(`/email-templates/${id}`, { method: "DELETE" });

export const duplicateEmailTemplate = (id: number): Promise<IEmailTemplate> =>
  call<IEmailTemplate>(`/email-templates/${id}/duplicate`, { method: "POST" });

export const setEmailTemplateActive = (id: number, isActive: boolean): Promise<IEmailTemplate> =>
  call<IEmailTemplate>(`/email-templates/${id}/active`, {
    method: "POST",
    body: JSON.stringify({ is_active: isActive }),
  });

export const setEmailTemplateDefault = (id: number): Promise<IEmailTemplate> =>
  call<IEmailTemplate>(`/email-templates/${id}/default`, { method: "POST" });

/* ── Preview ────────────────────────────────────────────────────── */

export const previewEmailTemplate = (
  id: number,
  leadId?: number | null,
): Promise<IEmailTemplatePreview> =>
  call<IEmailTemplatePreview>(`/email-templates/${id}/preview`, {
    method: "POST",
    body: JSON.stringify(leadId ? { lead_id: leadId } : {}),
  });

/* ── AI body generation ─────────────────────────────────────────── */

export type AITone   = "Professional" | "Friendly" | "Luxury" | "Formal" | "Warm" | "Corporate" | "Sales-focused";
export type AILength = "Short" | "Medium" | "Detailed";

export interface IAIGenerateInput {
  category?: string | null;
  category_label?: string | null;
  name?: string;
  subject?: string;
  tone?: AITone;
  length?: AILength;
  purpose?: string;
}

export interface IAIGenerateResult {
  body: string;
  variables: string[];
}

export const aiGenerateTemplateBody = (input: IAIGenerateInput): Promise<IAIGenerateResult> =>
  call<IAIGenerateResult>(`/email-templates/ai-generate`, {
    method: "POST",
    body: JSON.stringify(input),
  });

/* ── AI draft reply (composer "✨ AI Draft" button) ────────────────── */

export interface IAIDraftReplyInput {
  thread_id?: number | null;
  lead_id?: number | null;
  tone?: AITone;
  length?: AILength;
  purpose?: string;
  subject?: string;
}

export interface IAIDraftReplyResult {
  /** Body with {{tokens}} already resolved against lead/sender context.
   *  This is the value the composer paste into the editor by default. */
  body: string;
  /** Pre-resolution body — useful if a caller wants to preserve the
   *  placeholders (e.g. to save as a new template later). */
  raw_body: string;
  /** Tokens the AI actually used. */
  variables: string[];
  /** Lead the draft was personalized against, when one was resolved. */
  used_lead_id: number | null;
}

export const aiDraftReply = (input: IAIDraftReplyInput): Promise<IAIDraftReplyResult> =>
  call<IAIDraftReplyResult>(`/email-templates/ai-draft-reply`, {
    method: "POST",
    body: JSON.stringify(input),
  });

/* ── Render a saved template against a specific lead ──────────────── */

export interface IRenderForLeadResult {
  subject: string;
  body: string;
  body_text: string;
  template: IEmailTemplate;
}

export const renderTemplateForLead = (
  templateId: number,
  leadId: number,
): Promise<IRenderForLeadResult> =>
  call<IRenderForLeadResult>(`/email-templates/${templateId}/render-for-lead`, {
    method: "POST",
    body: JSON.stringify({ lead_id: leadId }),
  });
